import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../../components/feature/Layout';
import { fetchMaterialsList } from '../../repositories/materials';
import { useAuth } from '../../hooks/useAuth';
import type { Material, MaterialSortKey, MaterialFilter } from '../../types/materials';
import { SUBJECT_ICON_MAP, getGradeRangeLabel } from '../../constants/subjects';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

// 学年を表示用ラベルに変換
const getGradeLabel = (grade: number): string => {
  if (grade <= 6) return `小${grade}`;
  if (grade <= 9) return `中${grade - 6}`;
  return `高${grade - 9}`;
};

function MaterialsPage() {
  const { loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // ソート＆フィルター用の state
  const [sortKey, setSortKey] = useState<MaterialSortKey>('recommended');
  const [filter, setFilter] = useState<MaterialFilter>({
    subjects: [],
    gradeRange: { min: null, max: null },
    tags: [],
  });
  const [searchQuery, setSearchQuery] = useState('');

  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // SEO設定
  useSEO({
    title: '単元ガイド一覧｜キョウコレ',
    description: '学年や教科から、ぴったりの単元ガイドを見つけましょう。学習支援・こどもの居場所での学びをサポートする教材情報が満載です。',
    keywords: '単元ガイド,教材,学習支援,教科,学年,算数,数学,国語,英語,理科,社会',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/materials',
    jsonLd: generateWebPageSchema(
      '単元ガイド一覧｜キョウコレ',
      '学年や教科から、ぴったりの単元ガイドを見つけましょう。学習支援・こどもの居場所での学びをサポートする教材情報が満載です。',
      '/materials'
    ),
  });

  // URLクエリパラメータから初期値を設定（初回のみ）
  useEffect(() => {
    const subject = searchParams.get('subject');
    const grade = searchParams.get('grade');
    const q = searchParams.get('q');

    const initialFilter: MaterialFilter = {
      subjects: subject ? [subject] : [],
      gradeRange: grade ? { min: Number(grade), max: Number(grade) } : { min: null, max: null },
      tags: [],
    };

    setFilter(initialFilter);
    setSearchQuery(q || '');
  }, []); // 初回のみ実行

  // 一覧取得
  const loadMaterials = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('📚 単元ガイド一覧を取得中...', { sortKey, filter, searchQuery });

      const data = await fetchMaterialsList({
        sort: sortKey,
        filter,
        limit: 50,
        offset: 0,
      });

      // クライアント側で検索フィルタを適用
      let filteredData = data;
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filteredData = data.filter((material) => {
          const titleMatch = material.title.toLowerCase().includes(query);
          const descMatch = material.description.toLowerCase().includes(query);
          const tagsMatch = material.tags?.some((tag) => tag.toLowerCase().includes(query));
          return titleMatch || descMatch || tagsMatch;
        });
      }

      console.log('✅ 単元ガイド取得成功:', filteredData.length, '件');
      setMaterials(filteredData);
    } catch (err: any) {
      console.error('❌ 単元ガイド取得エラー:', err);
      setError(err.message ?? '単元ガイドの読み込みに失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [sortKey, filter, searchQuery]);

  useEffect(() => {
    if (authLoading) {
      console.log('⏳ 認証状態の確定を待機中...');
      return;
    }
    loadMaterials();
  }, [authLoading, loadMaterials]);

  // URLパラメータを更新（履歴を汚染しない）
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (filter.subjects && filter.subjects.length > 0) {
      params.set('subject', filter.subjects[0]);
    }
    
    if (filter.gradeRange?.min != null) {
      params.set('grade', String(filter.gradeRange.min));
    }
    
    if (searchQuery.trim()) {
      params.set('q', searchQuery);
    }

    // パラメータがある場合のみURLを更新
    if (params.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [filter, searchQuery]);

  // タグ候補を生成（既存の materials から）
  const allTags = Array.from(
    new Set(materials.flatMap((m) => m.tags || []))
  ).sort();

  // 教科の候補
  const subjectOptions = ['国語', '算数', '数学', '理科', '社会', '英語'];

  // 学年の候補（1〜12）
  const gradeOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  // フィルターハンドラ
  const handleSubjectToggle = (subject: string) => {
    setFilter((prev) => {
      const exists = prev.subjects?.includes(subject);
      const subjects = exists
        ? prev.subjects!.filter((s) => s !== subject)
        : [...(prev.subjects ?? []), subject];
      return { ...prev, subjects };
    });
  };

  const handleGradeRangeChange = (part: 'min' | 'max', value: string) => {
    const num = value ? Number(value) : null;
    setFilter((prev) => ({
      ...prev,
      gradeRange: {
        ...prev.gradeRange,
        [part]: num,
      },
    }));
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions, (option) => option.value);
    setFilter((prev) => ({ ...prev, tags: selectedOptions }));
  };

  const handleSortChange = (value: string) => {
    setSortKey(value as MaterialSortKey);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // 絞り込み条件をリセット
  const handleResetFilters = () => {
    setFilter({
      subjects: [],
      gradeRange: { min: null, max: null },
      tags: [],
    });
    setSearchQuery('');
    setSearchParams({}, { replace: true });
  };

  // 認証状態確定中 + データ読み込み中
  const isLoading = authLoading || loading;

  // 絞り込み条件が設定されているか
  const hasActiveFilters = 
    (filter.subjects && filter.subjects.length > 0) ||
    filter.gradeRange?.min != null ||
    filter.gradeRange?.max != null ||
    (filter.tags && filter.tags.length > 0) ||
    searchQuery.trim() !== '';

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">単元ガイド一覧</h1>

          {/* コントロールバー */}
          {!authLoading && (
            <div className="mb-6 bg-white rounded-xl shadow-sm border border-slate-100 p-4">
              {/* 検索ボックス */}
              <div className="mb-4">
                <label htmlFor="search-input" className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                  キーワード検索
                </label>
                <input
                  id="search-input"
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="単元名、説明、タグで検索..."
                  className="block w-full rounded-md border-slate-300 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500"
                />
              </div>

              <div className="flex flex-col lg:flex-row gap-4 lg:items-end lg:justify-between">
                {/* 左側：絞り込み */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* 教科 */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                      教科
                    </label>
                    <div className="space-y-1">
                      {subjectOptions.map((subject) => (
                        <label key={subject} className="flex items-center text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filter.subjects?.includes(subject) ?? false}
                            onChange={() => handleSubjectToggle(subject)}
                            className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="ml-2">{subject}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* 学年範囲 */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                      学年範囲
                    </label>
                    <div className="space-y-2">
                      <div>
                        <label htmlFor="grade-min" className="block text-xs text-slate-500 mb-1">
                          下限
                        </label>
                        <select
                          id="grade-min"
                          value={filter.gradeRange?.min ?? ''}
                          onChange={(e) => handleGradeRangeChange('min', e.target.value)}
                          className="block w-full rounded-md border-slate-300 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500 cursor-pointer"
                        >
                          <option value="">指定なし</option>
                          {gradeOptions.map((grade) => (
                            <option key={grade} value={grade}>
                              {getGradeLabel(grade)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="grade-max" className="block text-xs text-slate-500 mb-1">
                          上限
                        </label>
                        <select
                          id="grade-max"
                          value={filter.gradeRange?.max ?? ''}
                          onChange={(e) => handleGradeRangeChange('max', e.target.value)}
                          className="block w-full rounded-md border-slate-300 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500 cursor-pointer"
                        >
                          <option value="">指定なし</option>
                          {gradeOptions.map((grade) => (
                            <option key={grade} value={grade}>
                              {getGradeLabel(grade)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* タグ */}
                  <div>
                    <label htmlFor="tags-filter" className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                      タグ
                    </label>
                    <select
                      id="tags-filter"
                      multiple
                      value={filter.tags ?? []}
                      onChange={handleTagsChange}
                      className="block w-full rounded-md border-slate-300 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500 cursor-pointer"
                      size={5}
                    >
                      {allTags.map((tag) => (
                        <option key={tag} value={tag}>
                          {tag}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-slate-500">
                      Ctrl/Cmd を押しながら複数選択
                    </p>
                  </div>
                </div>

                {/* 右側：ソート */}
                <div className="lg:w-48">
                  <label htmlFor="sort-select" className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                    並び順
                  </label>
                  <select
                    id="sort-select"
                    value={sortKey}
                    onChange={(e) => handleSortChange(e.target.value)}
                    className="block w-full rounded-md border-slate-300 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500 cursor-pointer"
                  >
                    <option value="recommended">おすすめ順</option>
                    <option value="newest">新着順</option>
                    <option value="title_asc">タイトル順（あいうえお）</option>
                    <option value="grade_low_high">学年の低い順</option>
                    <option value="grade_high_low">学年の高い順</option>
                  </select>
                </div>
              </div>

              {/* リセットボタン */}
              {hasActiveFilters && (
                <div className="mt-4 text-center">
                  <button
                    onClick={handleResetFilters}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    絞り込みをリセット
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 検索結果件数 */}
          {!isLoading && !error && materials.length > 0 && (
            <div className="mb-4 text-sm text-slate-600">
              {materials.length}件の単元ガイドが見つかりました
            </div>
          )}

          {/* ローディング */}
          {isLoading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              <p className="mt-4 text-gray-600">
                {authLoading ? '認証状態を確認中…' : '読み込み中…'}
              </p>
            </div>
          )}

          {/* エラー */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* 0件表示 */}
          {!isLoading && !error && materials.length === 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8 text-center">
              <i className="ri-search-line text-6xl text-gray-400 mb-4"></i>
              <p className="text-gray-600 mb-2">条件に合う単元ガイドが見つかりませんでした。</p>
              <p className="text-sm text-gray-500 mb-4">絞り込み条件を少しゆるめて再度お試しください。</p>
              {hasActiveFilters && (
                <button
                  onClick={handleResetFilters}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line mr-2"></i>
                  絞り込みをリセット
                </button>
              )}
            </div>
          )}

          {/* 一覧表示 */}
          {!isLoading && !error && materials.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {materials.map((material) => {
                const icon = SUBJECT_ICON_MAP[material.subject];
                
                // slug が存在しない場合は詳細リンクを無効化
                const hasValidSlug = material.slug && material.slug.trim() !== '';
                const detailUrl = hasValidSlug ? `/materials/${material.slug}` : '#';
                
                return (
                  <div
                    key={material.id}
                    className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-slate-100 overflow-hidden"
                  >
                    <div className="p-6">
                      {/* 教科アイコン + 学年 */}
                      <div className="flex items-center gap-2 mb-4">
                        {icon && (
                          <div className={`flex h-9 w-9 items-center justify-center rounded-full text-lg ${icon.bgClass}`}>
                            <span aria-hidden="true">{icon.emoji}</span>
                          </div>
                        )}
                        <div className="flex flex-col">
                          <span className={`text-xs font-semibold ${icon?.textClass || 'text-gray-700'}`}>
                            {material.subject}
                          </span>
                          <span className="text-xs text-gray-500">
                            {getGradeRangeLabel(material.grade_min, material.grade_max)}
                          </span>
                        </div>
                      </div>

                      <h2 className="text-base font-semibold text-gray-900 mb-3 line-clamp-2">
                        {material.title}
                      </h2>

                      <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                        {material.description.substring(0, 80)}
                        {material.description.length > 80 ? '…' : ''}
                      </p>

                      {material.tags && material.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {material.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700"
                            >
                              {tag}
                            </span>
                          ))}
                          {material.tags.length > 3 && (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                              +{material.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      {hasValidSlug ? (
                        <Link
                          to={detailUrl}
                          className="inline-block w-full text-center px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                        >
                          単元ガイドを見る
                        </Link>
                      ) : (
                        <button
                          disabled
                          className="inline-block w-full text-center px-4 py-2 bg-gray-300 text-gray-500 text-sm font-medium rounded-lg cursor-not-allowed whitespace-nowrap"
                          title="この単元ガイドは現在利用できません"
                        >
                          利用不可
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default MaterialsPage;
